export class ContractError extends Error {
  constructor(code = 'invalid_contract', field = 'input') {
    super(`${code}: ${field}`);
    this.name = 'ContractError';
    this.code = code;
    this.field = field;
  }
}

export const reject = (field, code) => {
  throw new ContractError(code, field);
};
export const check = (test, field, code) => test || reject(field, code);
export function record(value, field = 'input') {
  check(value !== null && typeof value === 'object' && !Array.isArray(value), field);
  check([Object.prototype, null].includes(Object.getPrototypeOf(value)), field);
  for (const descriptor of Object.values(Object.getOwnPropertyDescriptors(value)))
    check(!descriptor.get && !descriptor.set && descriptor.enumerable, field);
  check(Object.getOwnPropertySymbols(value).length === 0, field);
  return value;
}
export function shape(value, required, optional = [], field = 'input') {
  record(value, field);
  for (const key of required) check(Object.hasOwn(value, key), `${field}.${key}`);
  const allowed = new Set([...required, ...optional]);
  for (const key of Object.keys(value)) check(allowed.has(key), field);
  return value;
}
export function text(value, field, max = 256) {
  check(typeof value === 'string' && value.length > 0 && value.length <= max, field);
  check(!/[\u0000-\u001f\u007f]/u.test(value), field);
  return value;
}
export function id(value, field) {
  text(value, field);
  check(!value.startsWith('/') && !value.includes('://'), field);
  return value;
}
export function digest(value, field) {
  check(typeof value === 'string' && /^[a-f0-9]{64}$/u.test(value), field);
  return value;
}
export function integer(value, field, min = 0, max = Number.MAX_SAFE_INTEGER) {
  check(Number.isSafeInteger(value) && value >= min && value <= max, field);
  return value;
}
export const boolean = (value, field) => check(typeof value === 'boolean', field);
export function choice(value, allowed, field) {
  check(allowed.includes(value), field);
  return value;
}
export function instant(value, field) {
  text(value, field, 32);
  check(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/u.test(value), field);
  const time = Date.parse(value);
  check(Number.isFinite(time), field);
  check(new Date(time).toISOString().slice(0, 19) === value.slice(0, 19), field);
  return value;
}
export function nullable(value, validate, field) {
  return value === null ? null : validate(value, field);
}
export function list(value, validate, field, max = 64) {
  check(Array.isArray(value) && value.length <= max, field);
  array(value, field);
  value.forEach((item, index) => validate(item, `${field}.${index}`));
  return value;
}
function array(value, field) {
  check(Object.getPrototypeOf(value) === Array.prototype, field);
  check(Object.getOwnPropertySymbols(value).length === 0, field);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  check(Object.keys(descriptors).length === value.length + 1, field);
  for (let i = 0; i < value.length; i++) {
    const descriptor = descriptors[i];
    check(descriptor && !descriptor.get && !descriptor.set && descriptor.enumerable, field);
  }
}
export function paired(left, right, field) {
  check((left === null) === (right === null), field);
}

function canonical(value, depth) {
  check(depth <= 16, 'payload_depth');
  if (value === null || typeof value === 'boolean') return JSON.stringify(value);
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') {
    check(Number.isFinite(value), 'payload_number');
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    check(value.length <= 1000, 'payload_count');
    array(value, 'payload_array');
    return `[${value.map((item) => canonical(item, depth + 1)).join(',')}]`;
  }
  record(value, 'payload');
  const keys = Object.keys(value).sort();
  check(keys.length <= 1000, 'payload_count');
  return `{${keys.map((key) => `${JSON.stringify(key)}:${canonical(value[key], depth + 1)}`).join(',')}}`;
}
export function canonicalJson(value, maxBytes = 1_048_576) {
  const result = canonical(value, 0);
  check(new TextEncoder().encode(result).byteLength <= maxBytes, 'payload_bytes');
  return result;
}
export function immutable(value) {
  canonicalJson(value);
  const copy = structuredClone(value);
  const freeze = (item) => {
    if (item && typeof item === 'object') {
      Object.values(item).forEach(freeze);
      Object.freeze(item);
    }
    return item;
  };
  return freeze(copy);
}
